/* istanbul ignore file */
import logger from '@adc/logger'
import _ from 'lodash'
import { StepId, Transition, WorkflowStep } from '../steps/types'

export const getWorkflowConfig = (
  featureFlags: Record<string, any>
): { steps: WorkflowStep[] } | null => {
  const workflowConfig =
    featureFlags['fe.resi.ab.testing.on.conversational.flow.step.order']
      ?.workflow
  if (!workflowConfig) {
    logger.error(
      'No workflow configuration found in feature flags, using fallback navigation'
    )
  }
  return workflowConfig || null
}

export const evaluateCondition = (
  answer: Record<string, any>,
  transition: Transition
): boolean => {
  if (transition.default) {
    return true
  }
  if (!transition.field) {
    return false
  }

  const fieldPath = transition.field.split('.')
  let value = answer

  if (answer[transition.field] !== undefined) {
    value = answer[transition.field]
  } else {
    try {
      for (const path of fieldPath) {
        if (!value || typeof value !== 'object') {
          return transition.operator === 'not_exists'
        }
        value = value[path]
      }
    } catch (e) {
      // If any errors occur during navigation, field doesn't exist
      return transition.operator === 'not_exists'
    }
  }

  if (transition.operator === 'not_exists') {
    return value === undefined || value === null
  }

  if (transition.operator === 'exists') {
    return value !== undefined && value !== null
  }

  // If we get here and the property is undefined, condition can't be met
  if (value === undefined || value === null) {
    return false
  }

  // Value comparison operators
  switch (transition.operator) {
    case 'equals':
      return value === transition.value
    case 'not_equals':
      return value !== transition.value
    case 'contains':
      return Array.isArray(value) && value.includes(transition.value)
    default:
      return false
  }
}

// Guard against infinite recursion when sellerSteps.next() calls back
// into determineNextStep
let _isInFallback = false

export const determineNextStep = (
  currentStepId: StepId,
  answer: Record<string, any>,
  featureFlags: Record<string, any>,
  sellerSteps: Map<StepId, any>
): StepId => {
  const workflowConfig = getWorkflowConfig(featureFlags)

  // Build fallback from sellerSteps, but guard against re-entry
  const currentStep = sellerSteps.get(currentStepId)
  const sellerStepNext = (): StepId | null => {
    if (
      _isInFallback ||
      !currentStep ||
      typeof currentStep.next !== 'function'
    ) {
      return null
    }
    _isInFallback = true
    try {
      return currentStep.next({ answer, featureFlags })
    } finally {
      _isInFallback = false
    }
  }

  if (!workflowConfig) {
    logger.info('No workflow config, falling back to THANK_YOU directly')
    return StepId.ThankYou
  }
  const currentWorkflowStep = workflowConfig.steps.find(
    (step: WorkflowStep) => step.id === currentStepId
  )

  if (!currentWorkflowStep) {
    logger.error(`Step ID "${currentStepId}" not found in workflow config`)
    return sellerStepNext() ?? StepId.ThankYou
  }

  try {
    if (
      !currentWorkflowStep.transitions ||
      !Array.isArray(currentWorkflowStep.transitions) ||
      !currentWorkflowStep.transitions.length
    ) {
      logger.warn(`No valid transitions for step ${currentStepId}`)
      return sellerStepNext() ?? StepId.ThankYou
    }
  } catch (e) {
    logger.error(`Malformed transition object for step ${currentStepId}`, e)
    return sellerStepNext() ?? StepId.ThankYou
  }

  // Find default transition for potential fallback
  let defaultTransition: Transition | null = null

  for (const transition of currentWorkflowStep.transitions) {
    if (transition.default) {
      defaultTransition = transition
      break
    }
  }

  // Try each non-default transition
  let hasValidTransitions = false
  for (const transition of currentWorkflowStep.transitions) {
    try {
      // Verify transition is well-formed
      if (!transition || typeof transition !== 'object') {
        continue
      }
      hasValidTransitions = true

      if (evaluateCondition(answer, transition)) {
        logger.info(
          `Matched condition for transition to ${transition.nextStepId}`
        )
        return transition.nextStepId
      }
    } catch (e) {
      // Skip malformed transitions
      logger.error(`Error evaluating transition condition:`, e)
      continue
    }
  }

  // No valid transitions found?
  if (!hasValidTransitions) {
    logger.warn(`No valid transitions found for step ${currentStepId}`)
    return sellerStepNext() ?? StepId.ThankYou
  }

  // Use default if available
  if (defaultTransition) {
    try {
      logger.info(`Using default transition to ${defaultTransition.nextStepId}`)
      return defaultTransition.nextStepId
    } catch (e) {
      // Malformed default transition
      logger.error(`Error using default transition:`, e)
    }
  }

  // Last resort fallback
  return sellerStepNext() ?? StepId.ThankYou
}
